import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  Action,
  ActionCreator,
  Creator,
  MemoizedSelector,
  Store,
} from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { filter, map } from 'rxjs/operators';
import { ActiveTenantStore } from '@arc/arc-shared-frontend';
import { CheckoutStep } from '@arc/unified-payments-frontend/shared-payment';
import { PaymentFooterActions } from '@arc/unified-payments-frontend/payment-footer';
import { selectIsAutopaySuggestionEligibilityCheckAllowed } from '../shared-selectors';

/**
 * Factory that creates a reusable NgRx functional effect deciding *when* to dispatch the
 * per-account Autopay eligibility check backing the Add Payment Method modal's Autopay
 * suggestion banner (BTPAY-7163). Deliberately not createAutopayEligibilityDispatchEffect
 * (Confirmation-only: hardcodes CheckoutStep.Confirmation, the full 4-condition gate, and a
 * one-time-PAD filter that doesn't apply pre-selection) — this fires once the buyer lands on
 * Select Payment Method, gated on selectIsAutopaySuggestionEligibilityCheckAllowed (tenant on
 * batched Autopay + no saved payment method yet).
 *
 * Usage (POI, POA — each passes its own trigger action and flow-local buyer-account-number(s)
 * selector, since that state lives in make-payment-core/pay-on-account, not in shared-stores):
 *
 *   export const poiSelectPaymentMethodAutopayEligibilityDispatchEffect =
 *     createAutopaySuggestionEligibilityDispatchEffect({
 *       checkEligibilityAction: PoiSelectPaymentMethodAutopayActions.checkAutopayEligibility,
 *       selectAccountNumbers: selectDocumentsBuyerAccountNumbers,
 *     });
 */
export function createAutopaySuggestionEligibilityDispatchEffect<
  A extends Action<string> & { tenantId: string; accountNumbers: string[] },
>(config: {
  checkEligibilityAction: ActionCreator<string, Creator<any[], A>>;
  selectAccountNumbers: MemoizedSelector<object, string[]>;
}) {
  return createEffect(
    (actions$ = inject(Actions), store = inject(Store)) =>
      actions$.pipe(
        ofType(PaymentFooterActions.CurrentCheckoutStepChangedAction),
        filter(
          (action) => action.payload.currentStep === CheckoutStep.PaymentMethod,
        ),
        concatLatestFrom(() => [
          store.select(selectIsAutopaySuggestionEligibilityCheckAllowed),
          store.select(ActiveTenantStore),
          store.select(config.selectAccountNumbers),
        ]),
        filter(
          ([, isEligibilityCheckAllowed, tenant, accountNumbers]) =>
            isEligibilityCheckAllowed &&
            !!tenant?.tenantId &&
            accountNumbers?.length > 0,
        ),
        map(
          ([, , tenant, accountNumbers]) =>
            config.checkEligibilityAction({
              tenantId: tenant.tenantId,
              accountNumbers,
            }) as Action,
        ),
      ),
    { functional: true },
  );
}
